import type { Project } from '@/types'

const img = (name: string): string => `/images/${name}`

export const projects: readonly Project[] = [
    {
        slug: 'unstable-sequence',
        index: '001',
        titleLines: ['Unstable', 'Sequence'],
        title: 'Unstable Sequence',
        subtitle: 'A motion study that resists resolution.',
        clientName: 'Self-initiated',
        clientType: 'Independent project',
        date: 'March 2025',
        released: 'Mar 2025',
        timeframe: '6 weeks',
        category: 'Inspiration',
        description:
            'Drawn from motion studies where clarity fades and rhythm fractures. Meaning appears in the gaps between frames, where instability becomes structural.',
        hero: img('DltZGI65ipwQaAhDnRmFMkpaGKQ.jpg'),
        tags: ['Motion study', 'Visual experiment', 'Sound-integrated sequence'],
        summary:
            'A looping motion work built around delay, repetition, and perceptual drift.',
        introduction:
            'Unstable Sequence explores motion without destination. Constructed as a continuous loop, the work avoids narrative closure in favour of sustained tension. Attention is held through rhythm, interruption, and temporal misalignment rather than progression or payoff.',
        approach: [
            'The project began with a set of simple motion rules.',
            'Speed, direction, and repetition were defined early, then deliberately disrupted to introduce instability and tension.',
            'Sequences were built through iteration. Frames were layered, removed, re-timed, and reassembled until instability felt structural rather than accidental. Decisions were guided by rhythm and perception, not symmetry or balance.'
        ],
        approachImage: {
            src: img('M2gBQuA6ryToDRBTGhFUDqZCjc.jpg'),
            alt: 'Layered frames from the Unstable Sequence motion studies',
            caption: 'Layers finding their balance'
        },
        approachNote:
            'Frames captured mid-iteration, before structure settled and outcomes became clear.',
        challengeHeadline:
            'The main challenge was sustaining tension without arrival.',
        challengeBody:
            'Removing narrative payoff risked visual fatigue or passive ambiguity. The work needed to remain engaging while actively resisting clarity, closure, or resolution.',
        challengeImages: [
            {
                src: img('xgOE7dJa3VVWimK36wSNbJDOg.jpg'),
                alt: 'A figure caught between two frames of movement',
                caption: 'Sharp edges holding a restless rhythm'
            },
            {
                src: img('oTqeyouIJkbUMwDnv5H39usFetQ.jpeg'),
                alt: 'A bloom held still inside a blurred field',
                caption: 'Quiet strength in full bloom'
            },
            {
                src: img('AxhTqsQi3wzHWSaFRAVPHD2SHH4.jpg'),
                alt: 'A portrait dissolving into motion',
                caption: 'Thoughts emerge where the face disappears'
            }
        ],
        solution: [
            'Instability served as the organizing structure, with irregularities defining the system instead of being corrected.',
            'Timing imperfections, visual drag, and misalignment became signals instead of errors. Irregularity was amplified to create anticipation rather than correction.'
        ],
        solutionImages: [
            {
                src: img('f42l96tlwu8IDpIR4cfJPhIfI.jpg'),
                alt: 'A body blurred past the point of recognition',
                caption: 'When movement erases identity'
            },
            {
                src: img('xTZlYbsEdTPMt9XATundrjoWZ90.jpg'),
                alt: 'A figure leaning out of its own outline',
                caption: 'Mind in motion, body left behind'
            }
        ],
        solutionClose:
            'The final sequence unites through rhythm, not completion, with coherence arising from repetition and delay rather than explanation.',
        achievements:
            'Unstable Sequence was presented across online and physical contexts.',
        achievementsBody:
            'The work encouraged extended viewing and repeat engagement, with audiences responding more to rhythm, timing, and atmosphere than to narrative interpretation.',
        testimonial: {
            quote: '“Unstable Sequence holds your attention in a quiet, almost unsettling way. It resists easy interpretation, yet feels deeply intentional. The work invites you to stay longer than you planned, noticing tension, rhythm, and subtle emotional weight with each loop.”',
            name: 'Anna Blake',
            company: 'Radio Star Media',
            photo: img('kRj0YuKJIE6hToAeq15afb9pH4.jpeg')
        },
        highlights: [
            {
                title: 'Repeat Viewing',
                body: 'Viewers spent longer than average looping the sequence, frequently replaying sections without linear progression or defined endpoints.'
            },
            {
                title: 'Interpretation Range',
                body: 'Responses varied widely, confirming the work’s openness. Feedback focused on sensation, tension, and temporal shifts rather than explicit meaning.'
            },
            {
                title: 'Cross-Format Interest',
                body: 'The piece was referenced across motion, sound, and installation discussions, demonstrating adaptability beyond a single medium or presentation format.'
            }
        ]
    },
    {
        slug: 'still-pressure',
        index: '002',
        titleLines: ['Still', 'Pressure'],
        title: 'Still Pressure',
        subtitle:
            'A photographic study of presence, pause, and contained force.',
        clientName: 'Vögel',
        clientType: 'Distribution',
        date: 'September 2025',
        released: 'Sep 2025',
        timeframe: '9 weeks',
        category: 'Inspiration',
        description:
            'Moments of stillness where tension remains present. Light, distance, and posture carry weight without action.',
        hero: img('ZAL97FAW4xkcgA9EHxM19e6wsOY.jpg'),
        tags: ['Photography', 'Visual study', 'Editorial series'],
        summary:
            'A held sequence of portraits where nothing moves and everything is about to.',
        introduction:
            'Still Pressure looks at the moment before release. Each frame was made at the end of a long hold, when posture starts to argue with intention. Nothing in the series resolves; the force stays inside the body and the picture simply refuses to let it out.',
        approach: [
            'The series began by removing every reason to move.',
            'Subjects were asked to hold a position far past comfort, then photographed only after the effort stopped being performed.',
            'Light was kept flat and directional at once, so the surface of a shoulder or a jaw could register strain without theatre. Frames were culled by feel rather than by expression, keeping only the ones where stillness read as pressure.'
        ],
        approachImage: {
            src: img('h8sixiviY1fFnSOgNyrAj94ALG8.jpeg'),
            alt: 'A held pose photographed at the end of a long take',
            caption: 'The hold, one breath before it breaks'
        },
        approachNote:
            'Contact sheets from the long holds, chosen for strain rather than expression.',
        challengeHeadline:
            'Stillness reads as absence until you give it weight.',
        challengeBody:
            'A motionless frame invites the eye to leave. The work had to make holding still feel like an active, costly decision rather than an empty one.',
        challengeImages: [
            {
                src: img('aK56biGr27sgC880rdREJrJgTnU.jpg'),
                alt: 'A figure standing rigidly against a plain wall',
                caption: 'Weight carried without a single gesture'
            },
            {
                src: img('DA0Q8AgIJP81uFWvLkpKSZo.jpeg'),
                alt: 'A close study of a tensed hand',
                caption: 'The hand gives the hold away'
            },
            {
                src: img('VfoXBzIkYN2pRlBE6oYYaS97Ec.jpg'),
                alt: 'A portrait lit flat and hard at the same time',
                caption: 'Light that describes effort, not mood'
            }
        ],
        solution: [
            'Distance became the instrument. Framing was pulled back until the body had room to be looked at rather than read.',
            'Contrast was flattened and grain retained, so the eye settles on surface and posture instead of drama. Each image was sequenced against a near-identical neighbour to make small shifts feel seismic.'
        ],
        solutionImages: [
            {
                src: img('bAWwNVziCexjLuSRptw4jwUUQ.jpeg'),
                alt: 'A pair of near-identical frames placed side by side',
                caption: 'Two frames apart, a world apart'
            },
            {
                src: img('rgK3PpbanQ4CVwmKdKQJ7HHUFc.jpeg'),
                alt: 'A wide, flatly lit study of a standing figure',
                caption: 'Room enough to notice the strain'
            }
        ],
        solutionClose:
            'The finished series holds together through repetition and restraint, where meaning accumulates across frames instead of arriving in any one of them.',
        achievements:
            'Still Pressure ran as a distributed editorial series and a single-room hang.',
        achievementsBody:
            'Readers returned to the same frames across separate sittings, responding to posture and light rather than to any story the pictures were asked to tell.',
        testimonial: {
            quote: '“There is almost nothing happening in these pictures and I could not stop looking at them. The restraint is the point. You start reading bodies the way you would read weather.”',
            name: 'Ines Halvorsen',
            company: 'Vögel Distribution',
            photo: img('0akgDfSw8ol9dq6XcrvLJFKJMs.jpg')
        },
        highlights: [
            {
                title: 'Extended Dwell',
                body: 'Readers stayed with individual frames far longer than the editorial average, returning to the same three images across separate sessions.'
            },
            {
                title: 'Quiet Circulation',
                body: 'The series spread through recommendation rather than promotion, reaching print pickup without a campaign behind it.'
            },
            {
                title: 'Sequence Integrity',
                body: 'Editors ran the set in full rather than excerpting it, confirming that the work reads as one held gesture.'
            }
        ]
    },
    {
        slug: 'surface-tension',
        index: '003',
        titleLines: ['Surface', 'Tension'],
        title: 'Surface Tension',
        subtitle:
            'A controlled study in restraint, pressure, and delayed release.',
        clientName: 'Atlas',
        clientType: 'Broadcasting',
        date: 'October 2025',
        released: 'Oct 2025',
        timeframe: '11 weeks',
        category: 'Inspiration',
        description:
            'Inspired by moments where form appears stable while force accumulates beneath the surface. Tension builds through restraint rather than movement.',
        hero: img('nY5kraItXTuws5zL4Nuhz9qLHfM.jpg'),
        heroVideo: img('surface-tension.mp4'),
        tags: ['Motion system', 'Visual identity', 'Digital application'],
        summary:
            'A motion system for a broadcaster built on holding back rather than paying off.',
        introduction:
            'Surface Tension is a moving identity for a network that never announces itself. Every transition was designed to arrive late, hold, and leave before it resolves, so the brand registers as pressure under the picture instead of decoration on top of it.',
        approach: [
            'The system started as a single rule: nothing snaps.',
            'Every easing curve was rebuilt so acceleration is long and deceleration is longer, putting the viewer permanently just before the landing.',
            'Type, rule and frame share one timing family. Because the parts move to the same law, a two-second ident and a ten-minute lower-third feel like the same object seen at different speeds.'
        ],
        approachImage: {
            src: img('uBxP9U1PLV67RuuwTdMdbEkQA.jpg'),
            alt: 'A surface held under even, building load',
            caption: 'Load applied evenly, held deliberately'
        },
        approachNote:
            'Timing tests run frame by frame, before the curves were locked into the system.',
        challengeHeadline:
            'A broadcast identity has to repeat without ever settling.',
        challengeBody:
            'Motion that resolves cleanly becomes wallpaper by the fourth viewing. The system needed to survive hundreds of repeats a day while still feeling like it has not finished.',
        challengeImages: [
            {
                src: img('eEXduufTWysqV1SPGfx1uvy19o.jpeg'),
                alt: 'A frame caught mid-transition',
                caption: 'Caught between two states, on purpose'
            },
            {
                src: img('2WRE967h2vJrkIS9mZJ3CRlpMI.jpeg'),
                alt: 'A wide plate used to test type against picture',
                caption: 'Type tested against the busiest picture'
            },
            {
                src: img('j0f0boTZxcbNRQJLpyezNUOKAEU.jpg'),
                alt: 'A vertical study of a held transition',
                caption: 'The hold is the identity'
            }
        ],
        solution: [
            'Release was rationed. Only one element in any sequence is allowed to complete its move, and it is never the one carrying the message.',
            'The rest hold a fraction short of their end state, which keeps the frame alive underneath the content and stops the system from resolving into decoration.'
        ],
        solutionImages: [
            {
                src: img('UjPdPzwyV1QCG0t3f7GTAsQ5h3c.jpg'),
                alt: 'A portrait plate used for on-air framing tests',
                caption: 'Framing rules, stress tested on air'
            },
            {
                src: img('TKjFE8fHS5OxcfEVOdm3sI2gEkI.jpeg'),
                alt: 'A wide banner plate from the broadcast package',
                caption: 'One law, read at every duration'
            }
        ],
        solutionClose:
            'The identity now runs across the full schedule as a single continuous pressure, recognisable in three frames and never quite finished.',
        achievements:
            'Surface Tension was rolled out across the network’s full on-air and digital schedule.',
        achievementsBody:
            'The system survives constant repetition because it never completes, holding the schedule together through timing rather than through any single mark or colour.',
        testimonial: {
            quote: '“We asked for something that could run a thousand times a week without going stale. What came back was a system that gets more interesting the more you see it, because it never lets you have the ending.”',
            name: 'Marcus Adeyemi',
            company: 'Atlas Broadcasting',
            photo: img('c1VocAo4m6CyMi0Q18oUoG4UAIM.jpg')
        },
        highlights: [
            {
                title: 'Schedule-Wide Adoption',
                body: 'The timing family was applied across idents, continuity, titles and digital within a single quarter, replacing four unrelated systems.'
            },
            {
                title: 'Repeat Tolerance',
                body: 'Audience testing showed no measurable fatigue after sustained daily exposure, the metric the network briefed for.'
            },
            {
                title: 'Three-Frame Recognition',
                body: 'Viewers identified the network from motion alone, before any logo or colour resolved on screen.'
            }
        ]
    },
    {
        slug: 'fragile-perfection',
        index: '004',
        titleLines: ['Fragile', 'Perfection'],
        title: 'Fragile Perfection',
        subtitle: 'A photographic study of composure and strain.',
        clientName: 'Maximo',
        clientType: 'Press',
        date: 'April 2025',
        released: 'Apr 2025',
        timeframe: '7 weeks',
        category: 'Inspiration',
        description:
            'Drawn from the tension between idealised beauty and the effort required to sustain it. The work looks at how composure is maintained, adjusted, and quietly tested through posture, stillness, and expectation.',
        hero: img('t07F4vqXxSysriJHzClQbCV6s78.jpeg'),
        tags: ['Photography', 'Portrait series', 'Artistic study'],
        summary:
            'Portraits made in the seconds around composure, not the second it is achieved.',
        introduction:
            'Fragile Perfection photographs the maintenance rather than the result. Every frame was taken either just before a pose locked or just after it slipped, so what survives is the labour of looking effortless instead of the effect of it.',
        approach: [
            'The sessions were built to outlast the pose.',
            'Each sitter was photographed continuously through the adjustments they would normally hide, and the frames they expected to be chosen were set aside first.',
            'Nothing was retouched toward symmetry. Hair out of place, a jaw held a beat too long and a shoulder correcting itself were treated as the subject rather than as faults in it.'
        ],
        approachImage: {
            src: img('VP537IhNaTLW7QOkMnGSrpCPh0.jpg'),
            alt: 'A sitter adjusting between two exposures',
            caption: 'The adjustment nobody keeps'
        },
        approachNote:
            'Frames taken between the poses, where the effort is still visible.',
        challengeHeadline:
            'Beauty photographs itself. Effort has to be given room.',
        challengeBody:
            'Every instinct in portraiture smooths this work away. The series had to stay generous to its subjects while refusing the flattery that would have erased the point.',
        challengeImages: [
            {
                src: img('mRBiQFDuE4B7QVSEklk21DWcM.jpeg'),
                alt: 'A portrait held a moment too long',
                caption: 'Held one beat past comfortable'
            },
            {
                src: img('gLqCslm3A6i3AkKnPkWHelWtkNo.jpg'),
                alt: 'A study of posture correcting itself',
                caption: 'A shoulder correcting itself'
            },
            {
                src: img('L61MvCY8ttcVvChrDXLqkdiAcFo.jpg'),
                alt: 'A wide frame of a sitter at rest between takes',
                caption: 'Rest, which is also a performance'
            }
        ],
        solution: [
            'Scale carried the argument. The in-between frames were printed large and the resolved ones small, inverting which version of a person gets authority.',
            'Sequencing pairs each sitter with themselves, so the viewer compares a body to its own effort rather than to anybody else’s outcome.'
        ],
        solutionImages: [
            {
                src: img('WgSXpdQNIRyyqbHxMdMqlYBg4k.jpeg'),
                alt: 'A large-format print of an unresolved frame',
                caption: 'The unresolved frame, printed largest'
            },
            {
                src: img('9Hbjl9ztDSwlBJuq6SREqQR8Dg.jpeg'),
                alt: 'A wide pairing of two frames of the same sitter',
                caption: 'A person, next to their own effort'
            }
        ],
        solutionClose:
            'The series reads as a single argument made twenty times: composure is work, and the work is the more interesting picture.',
        achievements:
            'Fragile Perfection ran as a long-form press feature and a touring hang.',
        achievementsBody:
            'Sitters and editors both gravitated to the unresolved frames, which settled the argument the series was built to make about where composure actually lives.',
        testimonial: {
            quote: '“It is a generous series and a difficult one. Nobody is caught out, but nobody is flattered either. You come away thinking about how much of what we call poise is simply somebody holding on.”',
            name: 'Delphine Okonkwo',
            company: 'Maximo Press',
            photo: img('iSdrAm67g930eJjF7obZPSRKy5k.jpg')
        },
        highlights: [
            {
                title: 'Full-Feature Run',
                body: 'The series ran uncut across sixteen pages, with the in-between frames given the same weight as the resolved ones.'
            },
            {
                title: 'Sitter Participation',
                body: 'Every subject approved the unresolved frames for publication, several choosing them over the composed alternatives.'
            },
            {
                title: 'Continued Touring',
                body: 'The hang has travelled to three venues, each re-sequencing the pairs without changing the argument.'
            }
        ]
    },
    {
        slug: 'silent-gravity',
        index: '005',
        titleLines: ['Silent', 'Gravity'],
        title: 'Silent Gravity',
        subtitle:
            'A visual study of weight, pull, and the slow force shaping human presence.',
        clientName: 'Self-initiated',
        clientType: 'Concept',
        date: 'May 2025',
        released: 'May 2025',
        timeframe: '14 weeks',
        category: 'Inspiration',
        description:
            'Inspired by moments where bodies, objects, and environments are shaped by forces they cannot negotiate — pressure, expectation, momentum, and time. Silent Gravity observes how weight doesn’t always crush.',
        hero: img('nUVUKUFiqlOWfRUrbkLvAGji3NM.jpg'),
        tags: [
            'Photography Series',
            'Motion Still Experiments',
            'Concept Development',
            'Studio Production'
        ],
        summary:
            'An ongoing record of the forces that shape a body without ever touching it.',
        introduction:
            'Silent Gravity treats weight as something that arrives from outside. Expectation, momentum and time all press on a figure the way mass does, and the work looks for the posture that answers them. Nothing in the series is crushed; everything in it is carrying.',
        approach: [
            'The work began by photographing load instead of effort.',
            'Figures were placed in relation to floors, walls and horizons that were slightly too close, so the environment could do the pressing.',
            'Motion stills were shot alongside the frames at matched exposures, which let a single gesture be read as both a held position and a movement that has not finished arriving.'
        ],
        approachImage: {
            src: img('3dhrjEKd33WmxaQ1Mtixa0iI3M4.jpeg'),
            alt: 'A figure pressed into a low, close horizon',
            caption: 'A horizon set deliberately too low'
        },
        approachNote:
            'Studio tests where the room, not the subject, supplied the weight.',
        challengeHeadline:
            'Force you cannot see has to be built into the frame.',
        challengeBody:
            'Nothing in the series is dramatic. Without a visible cause, the pressure had to come from composition, proximity and the angle a body chooses when it is already carrying something.',
        challengeImages: [
            {
                src: img('jjJ6XIKZWJ2rtvkJZuo8IqJCQHo.jpg'),
                alt: 'A figure disassembled by interference',
                caption: 'Interference doing the work of gravity'
            },
            {
                src: img('cKuTS8u7Z5a04Mq4liKz4JTSxiE.jpg'),
                alt: 'A portrait weighted by a low, heavy frame',
                caption: 'Pressed down by the frame itself'
            },
            {
                src: img('xgOE7dJa3VVWimK36wSNbJDOg.jpg'),
                alt: 'A body holding an angle it did not choose',
                caption: 'An angle the body did not choose'
            }
        ],
        solution: [
            'Proximity replaced spectacle. Surfaces were brought close enough to crowd the figure, so the pressure reads before the subject does.',
            'Stills and motion frames were sequenced against each other at the same scale, letting weight appear as duration in one image and as posture in the next.'
        ],
        solutionImages: [
            {
                src: img('Sm2fKVNy39fa04VTwPRWSRmF4o.jpeg'),
                alt: 'A figure folded inward inside a tight frame',
                caption: 'Folded inward, holding steady'
            },
            {
                src: img('xTZlYbsEdTPMt9XATundrjoWZ90.jpg'),
                alt: 'A wide study of a body resisting a slow pull',
                caption: 'A slow pull, answered slowly'
            }
        ],
        solutionClose:
            'Silent Gravity stays open by design. The series grows whenever a new force turns up worth carrying, and it resolves nothing on the way.',
        achievements:
            'Silent Gravity remains in production and has been shown in stages.',
        achievementsBody:
            'Each showing is treated as a state of the work rather than a conclusion, and the sequence is rebuilt around whatever the newest frames have started to press against.',
        testimonial: {
            quote: '“It is the rare body of work that gets heavier the longer you stand with it. Nothing is happening and yet everything in the frame is under load. I left thinking about my own posture.”',
            name: 'Tomas Lindqvist',
            company: 'Independent Curator',
            photo: img('IuY87rHFvg04b8zd74d08RW6sI.png')
        },
        highlights: [
            {
                title: 'Ongoing Production',
                body: 'The series has no fixed edit. New work joins it whenever the concept earns another frame, and earlier images are re-sequenced around it.'
            },
            {
                title: 'Staged Showings',
                body: 'Sections have been shown three times, each hang treated as a state of the work rather than a finished presentation.'
            },
            {
                title: 'Cross-Medium Reading',
                body: 'Stills and motion frames are consistently read as one body of work, confirming the matched-exposure method holds across formats.'
            }
        ]
    }
]

export const getProject = (slug: string): Project | undefined =>
    projects.find((project) => project.slug === slug)

export const getOtherProjects = (slug: string): readonly Project[] =>
    projects.filter((project) => project.slug !== slug)
